import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useMemo, useEffect, useState } from "react";
import { useGetCalendarEvents } from "src/services";
import ical from "ical.js";
import {
  Box,
  Typography,
  Button,
  Link,
  Icon,
  styled,
  Paper,
  Collapse,
  keyframes,
} from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import { customEvent } from "src/utils/gtag.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import {
  generateGCalReminder,
  getCalendarApiUrl,
  getWebcalFormat,
  useCheckSubscriptionStatus,
  copyCalendarLinkToClipboard,
  handleOpenWebcal,
} from "src/utils/Utils.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { useTheme } from "@emotion/react";
import LoadingAnimation from "src/components/blocks/LoadingAnimation.js";
import RecommendedCalendars from "src/components/blocks/RecommendedCalendars.js";
import { UpgradeConsumerButton } from "src/components/upgrades/consumer/UpgradeConsumerButton.js";
import { UpgradeCTA } from "src/components/upgrades/UpgradeCTA.js";
import NativeCheckoutComponent from "src/components/blocks/NativeCheckoutComponent.js";
import FAQAccordion from "../FAQAccordian.js";
import { CommentsList } from "src/components/upgrades/CommentsList.js";
import User2 from "src/assets/users/user2.png";
import BenefitsCarousel from "src/components/upgrades/BenefitsCarousel.js";
import DownloadSampleButton from "../DownloadSampleButton.js";

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
  100% {
    opacity: 1;
  }
`;

// Connected verification container (top section)
const VerificationContainer = styled(Paper)(({ theme }) => ({
  borderRadius: "20px 20px 0 0",
  overflow: "hidden",
  boxShadow: theme.shadows.cardMd(theme.palette),
  border: "none",
  backgroundColor: theme.palette.topSectionBg,
  padding: theme.spacing(4),
  position: "relative",
  transition: "all 0.5s ease-in-out",
}));

// Explanation container (bottom section)
const ExplanationContainer = styled(Paper)(({ theme }) => ({
  borderRadius: "0 0 20px 20px",
  overflow: "hidden",
  boxShadow: theme.shadows.cardLg(theme.palette),
  border: "none",
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4),
  marginTop: "-2px",
}));

const VerifyCalendarSubscription = () => {
  const { user } = useContext(AuthContext);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const location = useLocation();
  const calendar = location.state ? location.state.calendar ?? null : null;
  const isReminder = location.state?.isReminder ?? false;
  const navigate = useNavigate();
  const calendarApiUrl = getCalendarApiUrl({ calendar, user });
  const { inGroup } = useCheckSubscriptionStatus({
    user,
    calendar,
  });
  const theme = useTheme();
  const trialPeriodDays = 7;

  // State for the verification flow
  const [confirmed, setConfirmed] = useState(false);
  const [hideVerification, setHideVerification] = useState(false);

  const link = useMemo(
    () => calendar?.stanzaLink?.replace("webcal://", "http://") || "",
    [calendar?.stanzaLink]
  );

  const { data: calendarEvents, isLoading: isLoadingCalendarEvents } =
    useGetCalendarEvents(
      {
        link,
        updated: calendar?.updatedAt,
      },
      {
        enabled: !!calendar?.stanzaLink,
      }
    );

  const mostRecentEventDate = useMemo(() => {
    if (!calendarEvents) return null;

    try {
      const jcalData = ical.parse(calendarEvents);
      const comp = new ical.Component(jcalData);
      const vevents = comp.getAllSubcomponents("vevent");

      if (vevents.length === 0) return null;

      const now = new Date();
      let closestEvent = null;
      let smallestDiff = Infinity;

      vevents.forEach((event) => {
        const end = event.getFirstPropertyValue("dtend").toJSDate();
        // Only consider events that have not yet ended
        if (end > now) {
          const diff = end - now;
          if (diff < smallestDiff) {
            smallestDiff = diff;
            closestEvent = event;
          }
        }
      });

      if (!closestEvent) return null;

      const eventDate = closestEvent
        .getFirstPropertyValue("dtstart")
        .toJSDate();
      return eventDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [calendarEvents]);

  const handleConfirm = () => {
    // Track the "Yes, I see it" button click
    customEvent({
      name: "user_confirm_calendar_subscription",
      category: "calendar_subscription",
      label: isReminder ? "confirm_reminder" : "confirm_calendar",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });

    // Track landing on success (since we're combining the screens)
    customEvent({
      name: "landed_on_subscription_success",
      category: "CalendarSubscription",
      label: "Landed on Calendar Subscription Success Page",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });

    setConfirmed(true);
  };

  // Auto-hide verification section after showing success message
  useEffect(() => {
    if (confirmed) {
      const timer = setTimeout(() => {
        setHideVerification(true);
      }, 3000); // Hide after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [confirmed]);

  const handleResubscribe = () => {
    if (isReminder) {
      // Track the "Download the reminder again" click
      customEvent({
        name: "user_error_download_reminder_again",
        category: "CalendarSubscription",
        label: "Download Reminder Again",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
          calendar_id: calendar?._id,
          calendar_handle: calendar?.handle,
        },
      });

      // Handle downloading reminder again
      const stanzaLink = `${calendar?.stanzaLink}/${user?._id}.ics`;
      const icsContent = generateGCalReminder(calendar?.name, stanzaLink);
      const blob = new Blob([icsContent], {
        type: "text/calendar;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);

      // Create a temporary link to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.download = `Sync_${calendar?.name}_GCal_Reminder.ics`;

      // Append to the document and trigger the click
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Track the "Resubscribe" click
      customEvent({
        name: "user_error_resubscribe_calendar",
        category: "CalendarSubscription",
        label: "Resubscribe to Calendar",
        data: {
          user_id: user?._id,
          user_email: user?.email,
          user_handle: user?.handle ?? "no_handle",
          calendar_id: calendar?._id,
          calendar_handle: calendar?.handle,
        },
      });

      // Handle resubscribing to calendar
      handleOpenWebcal(user, getWebcalFormat(calendarApiUrl), calendar);
    }
  };

  const handleCopyToClipboard = () => {
    copyCalendarLinkToClipboard({
      inGroup,
      user,
      calendar,
      showSnackBar,
      setSnackbarOpen,
      setSnackbarMessage,
      setSnackbarSeverity,
    });
  };

  const handleUpgradeClick = () => {
    if (user) {
      // If user is logged in, scroll to payment section
      const paymentSection = document.getElementById("payment-section");
      if (paymentSection) {
        paymentSection.scrollIntoView({ behavior: "smooth" });
      }
    }
    // For logged out users, let the UpgradeConsumerButton handle the default behavior
  };

  useEffect(() => {
    customEvent({
      name: "landed_on_subscription_verification",
      category: "CalendarSubscription",
      label: "Landed on Subscription Verification",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: calendar?.handle,
      },
    });
  }, []);

  const description = isReminder
    ? `Follow the instructions in the reminder to subscribe to "${calendar?.name}" on your desktop, then sync it with the Google Calendar app on your phone.`
    : `Your calendar will now automatically update whenever ${calendar?.name} events are added, changed, or cancelled.`;

  return (
    <>
      {isLoadingCalendarEvents ? (
        <LoadingAnimation />
      ) : (
        <InstructionsLayout
          sx={{ alignItems: "flex-start", textAlign: "left" }}
        >
          {/* Step 1: Verification Section */}
          <Collapse in={!hideVerification}>
            <VerificationContainer>
              <Collapse in={!confirmed}>
                <Box>
                  <Typography
                    variant="h3"
                    sx={{ mb: 2, color: theme.palette.primary.contrastText }}
                  >
                    All Set?
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ mb: 4, color: theme.palette.overlay.white80 }}
                  >
                    {isReminder ? (
                      <>
                        Do you see a reminder in your calendar for later today?
                        <br />
                        <br />
                        It should include instructions for subscribing to{" "}
                        {calendar.name} on desktop and syncing on mobile.
                      </>
                    ) : (
                      <>
                        Do you see{" "}
                        <span
                          style={{
                            fontWeight: 500,
                            color: theme.palette.primary.contrastText,
                          }}
                        >
                          {calendar.name}
                        </span>{" "}
                        in your list of calendars
                        {mostRecentEventDate ? (
                          <>
                            {" "}
                            and an event on{" "}
                            <span
                              style={{
                                fontWeight: 500,
                                color: theme.palette.primary.contrastText,
                              }}
                            >
                              {mostRecentEventDate}
                            </span>
                            ?
                          </>
                        ) : (
                          "?"
                        )}
                      </>
                    )}
                  </Typography>

                  <Button
                    variant="contained"
                    size="grown"
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifySelf: "center",
                      mb: 2,
                      backgroundColor: `${theme.palette.secondary.main} !important`,
                      color: `${theme.palette.secondary.contrastText} !important`,
                      animation: `${pulse} 4s infinite`,
                      "&:hover": {
                        backgroundColor: `${theme.palette.overlay.white90} !important`,
                      },
                    }}
                    onClick={handleConfirm}
                  >
                    YES, I SEE IT
                  </Button>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      color: theme.palette.overlay.white90,
                    }}
                  >
                    Having issues?{" "}
                    <Link
                      onClick={handleResubscribe}
                      sx={{
                        cursor: "pointer",
                        color: theme.palette.primary.contrastText,
                        textDecoration: "underline",
                      }}
                    >
                      {isReminder
                        ? "Download the reminder again"
                        : "Resubscribe"}
                    </Link>{" "}
                    or{" "}
                    <Link
                      onClick={handleCopyToClipboard}
                      sx={{
                        cursor: "pointer",
                        color: theme.palette.primary.contrastText,
                        textDecoration: "underline",
                      }}
                    >
                      copy this link
                    </Link>{" "}
                    before{" "}
                    <Link
                      href="https://stanza-1.gitbook.io/stanza-help-center/for-subscribers/editor/issues-redirecting-to-your-calendar-app/manually-import-a-calendar"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        cursor: "pointer",
                        color: theme.palette.primary.contrastText,
                        textDecoration: "underline",
                      }}
                    >
                      troubleshooting
                    </Link>
                    .
                  </Typography>
                </Box>
              </Collapse>

              <Collapse in={confirmed}>
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <Icon
                    baseClassName="material-symbols-outlined"
                    sx={{
                      fontSize: "2rem",
                      color: theme.palette.primary.contrastText,
                      mr: 1,
                      verticalAlign: "middle",
                    }}
                  >
                    check_circle
                  </Icon>
                  <Typography
                    variant="h6"
                    sx={{
                      color: theme.palette.primary.contrastText,
                      fontWeight: 600,
                      display: "inline",
                      verticalAlign: "middle",
                    }}
                  >
                    You're all set.
                  </Typography>
                </Box>
              </Collapse>
            </VerificationContainer>
          </Collapse>

          {/* Step 2: What Happens Next */}
          <ExplanationContainer
            sx={{
              borderRadius: hideVerification ? "20px" : "0 0 20px 20px",
              marginTop: hideVerification ? 0 : "-2px",
            }}
          >
            {/* FAQs */}
            <FAQAccordion
              defaultExpanded={2}
              faqData={[
                {
                  question: "How frequently will my calendar update?",
                  answer:
                    "Depending on your calendar app, it can take up to 48 hours to get updates.",
                },
                {
                  question:
                    "Will I know when my calendar updated or what changed?",
                  answer: "No, all calendars update silently by default.",
                },
                {
                  question:
                    "Can I get faster updates and know when my calendar last updated?",
                  answer:
                    "Yes, with Stanza+ you can get faster updates and know exactly when your calendar refreshed and what changed.",
                },
              ]}
            />

            {/* Download Sample & Upgrade Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                mt: 4,
                mb: 4,
              }}
            >
              <DownloadSampleButton
                variant="outlined"
                fullWidth
              />
              <UpgradeConsumerButton
                sx={{ width: "100%" }}
                upgradeType="calendar_subscription_success"
                onClick={user ? handleUpgradeClick : undefined}
              >
                Upgrade to Stanza+
              </UpgradeConsumerButton>
            </Box>

            {/* Quote Section */}
            <CommentsList
              comments={[
                {
                  text: (
                    <>
                      What I love most? Stanza+ tells me{" "}
                      <b>
                        when my calendar was last updated with a timestamp in my
                        events
                      </b>
                      . No more guessing if a game or show time has changed—I
                      know I'm always in the loop.
                    </>
                  ),
                  src: User2,
                },
              ]}
            />
          </ExplanationContainer>

          {/* Payment Section - Connected to black section below */}
          {user && (
            <Box
              id="payment-section"
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                boxShadow: theme.shadows.cardLg(theme.palette),
                border: "none",
                backgroundColor: theme.palette.background.paper,
                paddingRight: theme.spacing(4),
                paddingLeft: theme.spacing(4),
                paddingBottom: theme.spacing(8),
                mb: -4,
                mt: 6,
              }}
            >
              <NativeCheckoutComponent
                type="consumer_annual"
                trialPeriodDays={trialPeriodDays}
              />
            </Box>
          )}

          {/* Additional Content */}
          <Box sx={{ mt: 0 }}>
            {/* Final Upgrade CTA */}
            <Box sx={{ mb: 2, textAlign: "center" }}>
              <BenefitsCarousel
                variant="dark"
                onUpgradeClick={user ? handleUpgradeClick : undefined}
              />
            </Box>

            {/* You might like section - moved to bottom */}
            {calendar && (
              <Box>
                <RecommendedCalendars
                  sourceCalendars={[calendar]}
                  displayMode="related"
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() =>
                    navigate(
                      getPageRoute(
                        "home",
                        "HOME",
                        {},
                        { tab: user?.tags?.length > 0 ? "foryou" : "discover" }
                      )
                    )
                  }
                  sx={{ mb: 4 }}
                >
                  Discover More Calendars
                </Button>
              </Box>
            )}

            {/* Final CTA at bottom */}
            <UpgradeCTA variant="dark" sx={{ mb: 4 }} />
          </Box>
        </InstructionsLayout>
      )}
    </>
  );
};

export default VerifyCalendarSubscription;
